// client/src/components/configuracoes/sinais/SinalForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { XCircle } from 'lucide-react';
import { useMutateSinais } from '../../../hooks/useSinais';
import { useCategoriasParaFormulario } from '../../../hooks/useCategorias';
import { useServicos } from '../../../hooks/useServicos';
import { useRecursos } from '../../../hooks/useRecursos';
import { useMe } from '../../../hooks/useAuth';
import { useTenantI18n } from '../../../hooks/useAccounts';
import { getCurrencySymbol } from '../../../utils/formatters';
import useAlert from '../../../hooks/useAlert';

const initialState = {
    nome: '',
    tipoAlvo: 'servico',
    escopo: 'categoria', // 'categoria' | 'item' — só controla a UI, não é salvo
    categoria: '',
    servico: '',
    recurso: '',
    tipoValor: 'percentual',
    valorPercentual: 100,
    valorFixo: '',
    vigenciaInicio: '',
    vigenciaFim: '',
    ativo: true,
};

const toDateInputValue = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().slice(0, 10);
};

const SinalForm = ({ onCloseForm, onSaveSuccess, current }) => {
    const { accountId } = useMe();
    const { setAlert } = useAlert();
    // ✅ ACHADO: rótulos fixos em "(R$)", sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);
    const { createSinal, updateSinal, isPending } = useMutateSinais();
    const [formData, setFormData] = useState(initialState);

    const isEditing = !!current;

    const { data: categoriasData } = useCategoriasParaFormulario(accountId, formData.tipoAlvo);
    const { data: servicosData } = useServicos({ account: accountId, ativo: true, limit: 200 }, { enabled: formData.tipoAlvo === 'servico' && formData.escopo === 'item' });
    const { data: recursosData } = useRecursos(accountId, { ativo: true, limit: 200 });

    const categorias = categoriasData || [];
    const servicos = servicosData?.data?.data || servicosData?.data || [];
    const recursos = recursosData?.data?.data || recursosData?.data || [];

    useEffect(() => {
        if (isEditing && current) {
            const categoriaId = current.categoria?._id || current.categoria?.id || current.categoria || '';
            const servicoId = current.servico?._id || current.servico?.id || current.servico || '';
            const recursoId = current.recurso?._id || current.recurso?.id || current.recurso || '';
            setFormData({
                nome: current.nome || '',
                tipoAlvo: current.tipoAlvo || 'servico',
                escopo: categoriaId ? 'categoria' : 'item',
                categoria: categoriaId,
                servico: servicoId,
                recurso: recursoId,
                tipoValor: current.tipoValor || 'percentual',
                valorPercentual: current.valorPercentual ?? 100,
                valorFixo: current.valorFixo ?? '',
                vigenciaInicio: toDateInputValue(current.vigenciaInicio),
                vigenciaFim: toDateInputValue(current.vigenciaFim),
                ativo: current.ativo !== undefined ? current.ativo : true,
            });
        } else {
            setFormData(initialState);
        }
    }, [current, isEditing]);

    const onChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }, []);

    const onChangeTipoAlvo = useCallback((e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, tipoAlvo: value, categoria: '', servico: '', recurso: '' }));
    }, []);

    const onChangeEscopo = useCallback((e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, escopo: value, categoria: '', servico: '', recurso: '' }));
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nome.trim()) {
            return setAlert('O campo Nome é obrigatório.', 'warning');
        }
        if (formData.escopo === 'categoria' && !formData.categoria) {
            return setAlert('Selecione uma categoria.', 'warning');
        }
        if (formData.escopo === 'item' && formData.tipoAlvo === 'servico' && !formData.servico) {
            return setAlert('Selecione um serviço.', 'warning');
        }
        if (formData.escopo === 'item' && formData.tipoAlvo === 'recurso' && !formData.recurso) {
            return setAlert('Selecione um recurso.', 'warning');
        }
        if (formData.tipoValor === 'percentual' && (formData.valorPercentual === '' || formData.valorPercentual === null)) {
            return setAlert('Informe o percentual do sinal.', 'warning');
        }
        if (formData.tipoValor === 'fixo' && (formData.valorFixo === '' || formData.valorFixo === null)) {
            return setAlert('Informe o valor fixo do sinal.', 'warning');
        }

        const dataToSubmit = {
            nome: formData.nome.trim(),
            tipoAlvo: formData.tipoAlvo,
            categoria: formData.escopo === 'categoria' ? formData.categoria : null,
            servico: formData.escopo === 'item' && formData.tipoAlvo === 'servico' ? formData.servico : null,
            recurso: formData.escopo === 'item' && formData.tipoAlvo === 'recurso' ? formData.recurso : null,
            tipoValor: formData.tipoValor,
            valorPercentual: formData.tipoValor === 'percentual' ? Number(formData.valorPercentual) : undefined,
            valorFixo: formData.tipoValor === 'fixo' ? Number(formData.valorFixo) : undefined,
            vigenciaInicio: formData.vigenciaInicio || null,
            vigenciaFim: formData.vigenciaFim || null,
            ativo: formData.ativo,
        };

        try {
            if (isEditing) {
                await updateSinal.mutateAsync({ id: current._id || current.id, data: dataToSubmit });
            } else {
                await createSinal.mutateAsync(dataToSubmit);
            }
            onSaveSuccess();
        } catch (error) {
            // erro já tratado pelo hook (setAlert no onError)
        }
    };

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">{isEditing ? `Editar Regra: ${formData.nome || '...'}` : 'Nova Regra de Sinal'}</h3>
                <button type="button" onClick={onCloseForm} title="Fechar Formulário" className="form-close-button">
                    <XCircle size={24} />
                </button>
            </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Dados Básicos</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor="nome" className="required">Nome</label>
                                <input type="text" name="nome" id="nome" value={formData.nome} onChange={onChange} required className="form-input" placeholder="Ex.: Sinal Dezembro - Depilação" maxLength="150" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="tipoAlvo" className="required">Tipo</label>
                                <select name="tipoAlvo" id="tipoAlvo" value={formData.tipoAlvo} onChange={onChangeTipoAlvo} className="form-input" disabled={isEditing}>
                                    <option value="servico">Serviço</option>
                                    <option value="recurso">Recurso (Locação)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Alvo da Regra</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor="escopo">Aplicar em</label>
                                <select name="escopo" id="escopo" value={formData.escopo} onChange={onChangeEscopo} className="form-input" disabled={isEditing}>
                                    <option value="categoria">Toda uma categoria</option>
                                    <option value="item">Um {formData.tipoAlvo === 'servico' ? 'serviço' : 'recurso'} específico</option>
                                </select>
                                <p className="form-hint">Regra de item específico sempre vence sobre a de categoria, quando as duas se aplicarem.</p>
                            </div>

                            {formData.escopo === 'categoria' ? (
                                <div className="form-group">
                                    <label htmlFor="categoria" className="required">Categoria</label>
                                    <select name="categoria" id="categoria" value={formData.categoria} onChange={onChange} className="form-input">
                                        <option value="">Selecione...</option>
                                        {categorias.map(cat => (
                                            <option key={cat._id || cat.id} value={cat._id || cat.id}>{cat.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : formData.tipoAlvo === 'servico' ? (
                                <div className="form-group">
                                    <label htmlFor="servico" className="required">Serviço</label>
                                    <select name="servico" id="servico" value={formData.servico} onChange={onChange} className="form-input">
                                        <option value="">Selecione...</option>
                                        {servicos.map(s => (
                                            <option key={s._id || s.id} value={s._id || s.id}>{s.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label htmlFor="recurso" className="required">Recurso</label>
                                    <select name="recurso" id="recurso" value={formData.recurso} onChange={onChange} className="form-input">
                                        <option value="">Selecione...</option>
                                        {recursos.map(r => (
                                            <option key={r._id || r.id} value={r._id || r.id}>{r.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Valor do Sinal</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor="tipoValor" className="required">Tipo de Valor</label>
                                <select name="tipoValor" id="tipoValor" value={formData.tipoValor} onChange={onChange} className="form-input">
                                    <option value="percentual">Percentual do preço</option>
                                    <option value="fixo">Valor fixo ({currencySymbol})</option>
                                </select>
                            </div>
                            {formData.tipoValor === 'percentual' ? (
                                <div className="form-group">
                                    <label htmlFor="valorPercentual" className="required">Percentual (%)</label>
                                    <input type="number" name="valorPercentual" id="valorPercentual" value={formData.valorPercentual} onChange={onChange} min="0" max="100" step="1" className="form-input" placeholder="Ex.: 100" />
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label htmlFor="valorFixo" className="required">Valor Fixo ({currencySymbol})</label>
                                    <input type="number" name="valorFixo" id="valorFixo" value={formData.valorFixo} onChange={onChange} min="0" step="0.01" className="form-input" placeholder="Ex.: 50.00" />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Vigência</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor="vigenciaInicio">Início</label>
                                <input type="date" name="vigenciaInicio" id="vigenciaInicio" value={formData.vigenciaInicio} onChange={onChange} className="form-input" />
                                <p className="form-hint">Deixe em branco para valer desde já.</p>
                            </div>
                            <div className="form-group">
                                <label htmlFor="vigenciaFim">Fim</label>
                                <input type="date" name="vigenciaFim" id="vigenciaFim" value={formData.vigenciaFim} onChange={onChange} className="form-input" />
                                <p className="form-hint">Deixe em branco para não ter validade (vale para sempre).</p>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-group">
                            <label className="checkbox-label">
                                <input type="checkbox" name="ativo" checked={formData.ativo} onChange={onChange} className="checkbox-input" />
                                <span className="checkbox-custom"></span>
                                <div>
                                    <div className="font-medium">Regra Ativa</div>
                                    <div className="text-sm text-gray-500">Regras inativas não são aplicadas em novos agendamentos.</div>
                                </div>
                            </label>
                        </div>
                    </div>

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">Cancelar</button>
                <button type="submit" className="action-button action-button-primary" disabled={isPending}>
                    {isPending ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Criar Regra')}
                </button>
            </div>
        </form>
    );
};

export default SinalForm;
